import React, { useCallback, useState } from "react";
import { FormHeader } from "@components/Header";
import { useNavigate } from "react-router-dom";
import { FormBody, FormContentContainer } from "@components/Container";
import { Box, CircularProgress } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useCreateBankTransferChallenge } from "@hooks";
import { useSnackbar } from "notistack";
import { tracker } from "@utils/openreplay";
import { Modal } from "@components/Modal";
import {
  ContentBox,
  LogoWrapper,
  LogoImage,
  TitleTypography,
  BodyTypography,
  SmallBodyTypography,
  SemiBold,
  ButtonsRow,
  StyledSecondaryActionButton,
  StyledBottomActionButton,
} from "./StartBankTransferScreen.styles";

export const StartBankTransferScreen: React.FC = () => {
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: createChallengeRequest } =
    useCreateBankTransferChallenge({
      onError: (error) => {
        setIsLoading(false);
        tracker?.event("BankTransferChallengeRequestedError", {
          source: "StartBankTransferScreen",
          error: error,
          errorMessage: error.message,
        });
        enqueueSnackbar("Error al generar la transferencia bancaria", {
          variant: "error",
        });
      },
    });

  const handleRealizarTransferencia = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmAccept = useCallback(async () => {
    tracker?.event("BankTransferChallengeRequested", {
      source: "StartBankTransferScreen",
    });
    setShowConfirmModal(false);
    setIsLoading(true);
    await createChallengeRequest();
  }, [createChallengeRequest]);

  return (
    <>
      <FormHeader useMekiDocIcon />
      <FormContentContainer>
        <FormBody justifyContent="center">
          <ContentBox>
            <LogoWrapper>
              <LogoImage src="/assets/logos/khipu.png" alt="Khipu" />
            </LogoWrapper>
            <TitleTypography>
              Verificación de identidad con transferencia
            </TitleTypography>
            <BodyTypography>
              Completa la verificación con una{" "}
              <SemiBold>transferencia bancaria a través de Khipu</SemiBold>{" "}
              (valor entre $2.000 y $3.000), que{" "}
              <SemiBold>será reembolsado automáticamente</SemiBold> en las
              próximas 24 hrs.
            </BodyTypography>
            <SmallBodyTypography>
              Khipu está regulado por la Comisión Mercado Financiero (CMF) Ley
              21.521.
            </SmallBodyTypography>
          </ContentBox>
          <ButtonsRow>
            <StyledSecondaryActionButton
              onClick={() => navigate("/app/solicitudes")}
              disabled={isLoading}
            >
              Volver al inicio
            </StyledSecondaryActionButton>
            <StyledBottomActionButton
              onClick={handleRealizarTransferencia}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Realizar transferencia"
              )}
            </StyledBottomActionButton>
          </ButtonsRow>
        </FormBody>
      </FormContentContainer>

      <Modal
        title={
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={1.5}
          >
            <ErrorOutlineIcon
              sx={{ fontSize: 40, width: 40, height: 40, color: "#ED6C02" }}
            />
            <TitleTypography style={{ textAlign: "center" }}>
              No cierres esta pestaña ni la de Khipu
            </TitleTypography>
          </Box>
        }
        content="Espera la redirección automática de vuelta a MekiDoc al finalizar, para completar la verificación."
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        buttonProps={{
          buttonText: "Aceptar",
          onClick: handleConfirmAccept,
          loading: isLoading,
          disabled: isLoading,
        }}
      />
    </>
  );
};
